/**
 * TypingIndicator Component
 *
 * Displays animated typing dots when users are typing in a conversation.
 */

import React from 'react';

const TYPING_DOT_DELAYS_MS = [0, 150, 300];

interface TypingIndicatorProps {
  userNames?: string[];
  className?: string;
}

/**
 * Format typing indicator text
 */
const getTypingText = (userNames: string[]): string => {
  if (userNames.length === 0) return '';
  if (userNames.length === 1) return `${userNames[0]} is typing`;
  if (userNames.length === 2) return `${userNames[0]} and ${userNames[1]} are typing`;
  return `${userNames[0]} and ${userNames.length - 1} others are typing`;
};

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  userNames = [],
  className = '',
}) => {
  if (userNames.length === 0) return null;

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={`${getTypingText(userNames)}`}
    >
      {/* Animated dots */}
      <div className="flex items-center gap-1">
        {TYPING_DOT_DELAYS_MS.map((delayMs) => (
          <span
            key={`typing-dot-${delayMs}`}
            className="w-2 h-2 rounded-full animate-bounce"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              animationDelay: `${delayMs}ms`,
              animationDuration: '1s',
            }}
          />
        ))}
      </div>

      {/* Typing text */}
      <span className="text-xs text-muted-foreground italic">
        {getTypingText(userNames)}
      </span>
    </div>
  );
};

export default TypingIndicator;
